/**
 * ThemeContext.js — Theme Management
 *
 * Manages the application colour theme ('light' | 'dark').
 *
 * Source of truth is the backend `SystemColor` user setting (option 1 = Light,
 * 2 = Dark). localStorage ('fleet-theme' + the settings bootstrap cache) only
 * provides the first-paint value so there is no flash of the wrong theme;
 * once the user's settings load from the API the theme reconciles to them.
 * Changing the theme persists through SettingsContext → EditUserSetting.
 *
 * Must be mounted inside SettingsProvider.
 *
 * Usage:
 *   const { theme, setTheme } = useTheme();
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSettings, SETTING_KEYS, readCachedSettingValue } from './SettingsContext';

// Backend SystemColor option id ↔ theme (see DefaultSetting optionsJson)
const THEME_BY_OPTION = { 1: 'light', 2: 'dark' };
const OPTION_BY_THEME = { light: 1, dark: 2 };

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const { getSetting, setSettingValue } = useSettings();

  // Lazy-initialise from the cached backend value (falling back to the legacy
  // 'fleet-theme' key) so the correct theme is applied before the first paint.
  const [theme, setThemeState] = useState(() => {
    const cached = readCachedSettingValue(SETTING_KEYS.SYSTEM_COLOR);
    if (cached != null && THEME_BY_OPTION[Number(cached)]) return THEME_BY_OPTION[Number(cached)];
    const stored = localStorage.getItem('fleet-theme');
    return stored === 'dark' ? 'dark' : 'light';
  });

  // Reconcile with the backend value once user settings load (or change,
  // e.g. edited from the System Settings view).
  const systemColorValue = getSetting(SETTING_KEYS.SYSTEM_COLOR)?.value;
  useEffect(() => {
    const backendTheme = THEME_BY_OPTION[systemColorValue];
    if (backendTheme) setThemeState(backendTheme);
  }, [systemColorValue]);

  // Apply the theme attribute to the document root and keep the legacy
  // localStorage key in sync for the first-paint fallback.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('fleet-theme', theme);
  }, [theme]);

  // Ref mirror so the functional form setTheme(prev => ...) can resolve
  // without putting side effects inside the state updater (StrictMode-safe).
  const themeRef = useRef(theme);
  useEffect(() => { themeRef.current = theme; }, [theme]);

  // Public setter: updates UI immediately and persists to the backend setting.
  const setTheme = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(themeRef.current) : next;
    setThemeState(resolved);
    if (OPTION_BY_THEME[resolved]) {
      setSettingValue(SETTING_KEYS.SYSTEM_COLOR, OPTION_BY_THEME[resolved]);
    }
  }, [setSettingValue]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
